import { Component, OnInit } from '@angular/core';
import { FormlyService, Script } from '@nx-ahs/formly/shared';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalService } from '@nx-ahs/shared/components';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormlyBuilderApiService } from '../../services/formly-builder-api.service';

@Component({
	selector: 'formly-script-list',
	templateUrl: './formly-script-list.component.html',
	styleUrls: ['./formly-script-list.component.scss'],
	standalone: false
})
export class FormlyScriptListComponent implements OnInit {
	scripts: Script[] = []; // Original list of scripts (includes deleted/not deleted scripts)
	filteredScripts: Script[] = []; // Includes the latest version of the active scripts (full list for filtering)
	paginatedScripts: Script[] = []; // Only the scripts to display on the current page
	scriptTagsMap = new Map<string, number[]>(); // Creates a map of the tags. Keys are the tags (no dups), Values are an array of scriptIds
	loading: boolean = true;
	error: string | null = null;
	searchTerm: string = '';
	currentPage: number = 0;
	pageSize: number = 25;
	activeScript: Script | null = null;
	selectedTags: string[] = []; // Stores the selected tags for filtering
	tagKeys: string[] = []; // Stores the keys (tags) from scriptTagsMap
	searchPlaceholder: string = '';

	constructor(
		private formlyService: FormlyService,
		private builderApiService: FormlyBuilderApiService,
		private matDialog: MatDialog,
		private confirmationModalService: ConfirmationModalService,
		private snackBar: MatSnackBar
	) {}

	async ngOnInit() {
		this.loadScripts();
	}

	/**
	 * Open a dialog to create a new script.
	 */
	public async addNewScript(): Promise<void> {
		try {
			// Overrides for the name and description fields
			const overrides = {
				name: {
					props: {
						label: 'Script Name',
						placeholder: 'Enter script name',
						value: '',
						required: true
					}
				},
				description: {
					props: {
						label: 'Description',
						placeholder: 'Enter script description',
						value: '',
						required: true
					}
				}
			};

			// Open the Formly modal dialog for user input
			const data = await this.builderApiService.openFormlyDialog(
				{
					scriptId: 1, // this is the script its opening for the dialog
					title: 'Create New Script',
					overrides: overrides,
					clientOnly: true
				},
				this.matDialog
			);

			if (data) {
				// Read the tags from the form data — the API now expects string[].
				const tagsValue: string[] = Array.isArray(data.model.tags) ? data.model.tags : [];

				const newScriptData: Script = {
					name: data?.model?.name,
					description: data?.model?.description,
					tags: tagsValue
				};

				// Call the service to create the script
				const newScript = await this.builderApiService.manageScript('add', newScriptData);

				// Handle success and update UI
				if (newScript) {
					this.showSuccess(`Script created successfully!`);

					this.scripts.push(newScript);

					// Update filteredScripts (only non-deleted scripts, latest version only, etc.)
					this.filteredScripts = this.getLatestScripts(this.scripts.filter((script) => !script.isDeleted));

					// Rebuild the tag-to-script map from the updated filtered list.
					this.scriptTagsMap = this.buildTagScriptMap(this.filteredScripts);

					// Update tagKeys to reflect any new tags added via the new script.
					this.tagKeys = Array.from(this.scriptTagsMap.keys()).sort();

					// Finally, update the paginated scripts view.
					this.updatePaginatedScripts();
				}
			}
		} catch (error) {
			console.error('Error creating script:', error);
			this.showError('Failed to create the script. Please try again.');
		}
	}

	/**
	 * Adds a tag to associate with the script.
	 */
	public async addTagToScript(script: Script): Promise<void> {
		try {
			const data = await this.builderApiService.openFormlyDialog(
				{
					scriptId: 4, // this is the script its opening for the dialog
					title: 'Add Tag',
					clientOnly: true
				},
				this.matDialog
			);

			if (data) {
				const newTag: string = data?.model?.tag.toUpperCase(); // force uppercase to prevent duplicated words with different cases

				// Check if the tag already exists for the scriptId
				if (this.scriptTagsMap.has(newTag) && this.scriptTagsMap.get(newTag)?.includes(script.scriptId)) {
					this.showError(`The tag "${newTag}" already exists for this script.`, 3000);
					return;
				}

				// Save the tag to the script
				const response = await this.builderApiService.addTagToScript(newTag, script.scriptId);

				// Update UI on success
				if (response) {
					this.showSuccess(response, 3000);

					// Update the script object itself with the new tag so the UI updates immediately.
					script.tags = [...(script.tags ?? []), newTag];

					// Update the scriptTagsMap dynamically
					this.scriptTagsMap = this.updateTagScriptMap(
						this.scriptTagsMap, // Existing map
						newTag, // New tag
						script.scriptId // Associated scriptId
					);

					// Update tagKeys dynamically if the new tag doesn't already exist
					if (!this.tagKeys.includes(newTag)) {
						this.tagKeys.push(newTag);
						this.tagKeys.sort();
					}

					// Update filteredScripts and paginatedScripts
					this.filteredScripts = this.getLatestScripts(this.scripts.filter((script) => !script.isDeleted));
					this.updatePaginatedScripts();
				} else this.showError(response, 3000);
			}
		} catch (error) {
			console.error('Error adding tag to script:', error);
			this.showError('Failed to add tag to the script. Please try again.');
		}
	}

	/**
	 * Updates a map where keys are tags and values are arrays of scriptIds.
	 * Ensures no duplicate keys, and each tag is associated with its respective scriptIds.
	 *
	 * @param map - The existing map to update. If null, a new map is created.
	 * @param newTag - A single tag to add dynamically.
	 * @param scriptId - The scriptId associated with the newTag.
	 * @returns The updated map of tags to scriptIds.
	 */
	public updateTagScriptMap(
		map: Map<string, number[]> | null,
		newTag: string,
		scriptId: number
	): Map<string, number[]> {
		const tagScriptMap = map || new Map<string, number[]>();

		// Handle dynamic addition of a new tag and scriptId
		if (tagScriptMap.has(newTag)) {
			const scriptIds = tagScriptMap.get(newTag)!;
			if (!scriptIds.includes(scriptId)) {
				scriptIds.push(scriptId);
			}
		} else {
			tagScriptMap.set(newTag, [scriptId]);
		}

		return tagScriptMap;
	}

	/**
	 * Builds the initial tag-to-script map from the list of filtered scripts.
	 * @param scripts - The list of scripts to process.
	 * @returns A new map of tags to scriptIds.
	 */
	private buildTagScriptMap(scripts: Script[]): Map<string, number[]> {
		const tagScriptMap = new Map<string, number[]>();

		for (const script of scripts) {
			for (const tag of script.tags ?? []) {
				if (tagScriptMap.has(tag)) {
					const scriptIds = tagScriptMap.get(tag)!;
					if (!scriptIds.includes(script.scriptId)) {
						scriptIds.push(script.scriptId);
					}
				} else {
					tagScriptMap.set(tag, [script.scriptId]);
				}
			}
		}

		return tagScriptMap;
	}

	public clearSearch(): void {
		this.searchTerm = '';
		this.applyFilters();
	}

	/**
	 * Deletes an existing script.
	 * Doesn't actually delete on the backend of Script.Script, just sets isDeleted flag to true.
	 */
	public async deleteScript(scriptId: number): Promise<void> {
		try {
			const confirmation = await this.confirmationModalService
				.showConfirmationDialog(
					'Delete Script', // Title
					`Are you sure you want to delete script ${scriptId}?`,
					false, // `messageIsHTML`
					true, // Show cancel button
					'Yes', // Confirm button text
					'No' // Cancel button text
				)
				.afterClosed()
				.toPromise();

			if (confirmation) {
				const scriptData: Script = { scriptId: scriptId };

				// Call the service to delete the script
				const result = await this.builderApiService.manageScript('delete', scriptData);

				if (result) {
					this.showSuccess(`Script deleted successfully!`, 3000);

					// Update the original list of scripts
					this.scripts = this.scripts.filter((script) => script.scriptId !== scriptId);

					// Update filteredScripts (exclude deleted scripts)
					this.filteredScripts = this.getLatestScripts(this.scripts.filter((script) => !script.isDeleted));

					// Update paginatedScripts to reflect the current page
					this.updatePaginatedScripts();
				}
			}
		} catch (error) {
			console.error('Error deleting script:', error.message);
			this.showError(`Failed to delete the script. Please try again.`, 3000);
		}
	}

	/**
	 * Duplicates an existing script and opens the form dialog to create a new one.
	 */
	public async duplicateScript(script: Script): Promise<void> {
		try {
			// Prefill the form with the existing script's data
			const overrides = {
				name: {
					props: {
						label: 'Script Name',
						placeholder: 'Enter script name',
						value: `${script.name} (Copy)`,
						required: true
					}
				},
				description: {
					props: {
						label: 'Description',
						placeholder: 'Enter script description',
						value: script.description,
						required: true
					}
				}
			};

			const data = await this.builderApiService.openFormlyDialog(
				{
					scriptId: 2, // this is the script its opening for the dialog
					title: 'Duplicate Script',
					overrides: overrides,
					clientOnly: true
				},
				this.matDialog
			);

			if (data) {
				// Create a new script object
				const newScriptData: Script = {
					name: data?.model?.name,
					description: data?.model?.description,
					scriptIdToCopy: script.scriptId
				};

				// Save the new duplicate script
				const newScript = await this.builderApiService.manageScript('add', newScriptData);

				// Update UI on success
				if (newScript) {
					this.showSuccess(`Script duplicated successfully!`);

					this.scripts.push(newScript);

					// Update filteredScripts and paginatedScripts
					this.filteredScripts = this.getLatestScripts(this.scripts.filter((script) => !script.isDeleted));
					this.updatePaginatedScripts();
				}
			}
		} catch (error) {
			console.error('Error duplicating script:', error);
			this.showError('Failed to duplicate the script. Please try again.');
		}
	}

	/**
	 * Returns one entry per scriptId, deduplicating any repeated entries, sorted ascending by scriptId.
	 */
	public getLatestScripts(scripts: any[]): any[] {
		const scriptsMap = new Map<number, any>();

		for (const script of scripts) {
			if (!scriptsMap.has(script.scriptId)) {
				scriptsMap.set(script.scriptId, script);
			}
		}

		return Array.from(scriptsMap.values()).sort((a, b) => a.scriptId - b.scriptId);
	}

	/**
	 * Applies both the tag filter and the search-term filter to the full list
	 * of non-deleted scripts. Only the latest version of each script is kept.
	 * The resulting filtered scripts are then stored in `filteredScripts`,
	 * and the paginated display is updated.
	 */
	public applyFilters(): void {
		// Start from the base list (non-deleted)
		let results = this.scripts.filter((script) => !script.isDeleted);

		// Apply the tag filter if tags are selected
		if (this.selectedTags.length > 0) {
			const scriptIds = new Set<number>();
			this.selectedTags.forEach((tag) => {
				const ids = this.scriptTagsMap.get(tag) || [];
				ids.forEach((id) => scriptIds.add(id));
			});
			results = results.filter((script) => scriptIds.has(script.scriptId));
		}

		// Apply the search filter if there is text
		if (this.searchTerm.trim()) {
			const search = this.searchTerm.toLowerCase();
			results = results.filter(
				(script) =>
					script.name.toLowerCase().includes(search) || script.description?.toLowerCase().includes(search)
			);
		}

		// Convert to the “latest scripts” only
		this.filteredScripts = this.getLatestScripts(results);

		// Update the UI
		this.updatePaginatedScripts();
	}

	public async loadScripts() {
		this.loading = true;
		try {
			const isReady = await this.formlyService.isReady();
			console.log('Formly Service isReady?: ', isReady);
			if (isReady) {
				// Fetch scripts
				this.scripts = await this.builderApiService.getAllScripts();

				// Filter out deleted scripts and only the latest version
				const nonDeletedScripts = this.scripts.filter((script) => !script.isDeleted);
				this.filteredScripts = this.getLatestScripts(nonDeletedScripts);

				// Generate the tag-to-script map
				this.scriptTagsMap = this.buildTagScriptMap(this.filteredScripts);
				this.tagKeys = Array.from(this.scriptTagsMap.keys()).sort();

				// Initialize pagination
				this.updatePaginatedScripts();
				console.log('Formly Scripts Loaded');
			} else {
				throw new Error('FormlyService is not ready');
			}
		} catch (err) {
			this.error = 'Failed to load scripts.';
			console.error(err);
		} finally {
			this.loading = false;
		}
	}

	/**
	 * Updates the paginatedScripts array to match the current page and page size.
	 */
	private updatePaginatedScripts(): void {
		this.paginatedScripts = this.filteredScripts.slice(
			this.currentPage * this.pageSize,
			this.currentPage * this.pageSize + this.pageSize
		);
	}

	/**
	 * Handles pagination changes triggered by the MatPaginator.
	 * Updates the current page and page size, and slices the filteredScripts array
	 * to only display the scripts corresponding to the selected page.
	 *
	 * @param event - The PageEvent containing the new page index and page size.
	 */
	public onPageChange(event: PageEvent) {
		this.currentPage = event.pageIndex; // Update the current page index
		this.pageSize = event.pageSize; // Update the page size
		this.updatePaginatedScripts(); // Update the paginated scripts
	}

	private showSuccess(message: string, duration: number = 3000) {
		this.snackBar.open(message, 'Close', {
			duration, // Time limit in milliseconds
			panelClass: ['success-snackbar']
		});
	}

	private showError(message: string, duration: number = 3000) {
		this.snackBar.open(message, 'Close', {
			duration, // Time limit in milliseconds
			panelClass: ['error-snackbar']
		});
	}

	/**
	 * Sets the active script when the menu is opened.
	 * @param script The script to be acted upon.
	 */
	public setActiveScript(script: Script): void {
		this.activeScript = script;
	}
}
